import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { UseFormGetValues, UseFormWatch } from 'react-hook-form';
import { useGiftStore } from '../../../store/gift-store';
import { useUIStore } from '../../../store/ui-store';
import GiftUser from './GiftUser';
import { Seo } from '../../../components/seo';
import StatusForm from './StatusForm';
import GiftTag from '../../../components/GiftTag';
import { useAuthStore } from '../../../store/auth-store';
import { Button } from '../../../components/inputs/button';
import useAlertStore from '../../../store/alert';
import { useAppContext } from '../../../App';
import { UserUtils } from '../../../utils/User';
import GiftTypeTag from '../../../components/GiftTypeTag';

export interface RefFormHandler {
  onSubmit: (data: unknown) => Promise<void>;
  getValues: UseFormGetValues<Record<string, string>>;
  watch?: UseFormWatch<Record<string, string>>;
  reset?: () => void;
}

function GiftDetailPage() {
  const { alert } = useAlertStore();
  const formRef = useRef<RefFormHandler>(null);
  const { id } = useParams();
  const { setTitle, setBreadcrumbs } = useUIStore();
  const { globalNotif } = useAppContext();
  const {
    currentGift,
    getDetailedGift,
    clearCurrentGift,
    deleteGift,
    deleteGiftStatus,
  } = useGiftStore();
  const navigate = useNavigate();
  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken?.payload, [idToken]);

  useEffect(() => {
    getDetailedGift(Number(id));
  }, [id, getDetailedGift]);

  useEffect(() => () => clearCurrentGift(), [clearCurrentGift]);

  useEffect(() => {
    const givername = currentGift?.givername || {};
    setTitle(`${givername}'s Gift`);

    setBreadcrumbs([{ label: 'All gifts', link: generatePath('/gifts') }]);
  }, [currentGift, setBreadcrumbs, setTitle]);

  const dateView = useMemo(() => {
    if (currentGift) {
      return DateTime.fromJSDate(new Date(currentGift.orderDate))
        .setZone('America/New_York')
        .toFormat('ccc, LLL d, yyyy hh:mm a ZZZZ');
    }
    return '';
  }, [currentGift]);

  const deleteGiftConfirm = useCallback(async () => {
    alert(
      'Delete Gift',
      `Are you sure you want to permanently delete this gift? If a test result has been uploaded for this order, it will also be deleted.

<i>Note: This cannot be undone. Once deleted, it cannot be recovered.</i>
    `,
      [
        { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
        {
          text: 'Yes',
          onClick: async () => {
            if (id) {
              await deleteGift({ id: Number.parseInt(id, 10) });
              globalNotif?.success({ message: `Gift successfully deleted.` });
              navigate('/gifts');
            }
          },
        },
      ],
    );
  }, [alert, deleteGift, id, navigate, globalNotif]);

  return (
    <>
      <Seo title={`Gift No: ${currentGift?.shopifyId || '-'}`} />
      <div className="p-8 max-w-[1300px]">
        <div className="shadow rounded-lg">
          <div className="border-b border-gray-300 py-[10px] px-[24px] flex justify-between items-center">
            <div>
              <div className="flex flex-row items-start">
                <h4 className="text-2xl font-semibold mr-2">
                  Gift No: {currentGift?.shopifyId}
                </h4>
                {currentGift ? <GiftTypeTag gift={currentGift} /> : null}
                {currentGift ? <GiftTag gift={currentGift} /> : null}
              </div>
              <span className="text-base text-gray-500">{dateView}</span>
            </div>
            <div>
              {UserUtils.isSuperUser(cUser) ? (
                <Button
                  label="Delete Gift"
                  onClick={deleteGiftConfirm}
                  loading={deleteGiftStatus.isLoading}
                />
              ) : null}
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-wrap flex-col-reverse md:flex-row">
            <div className="w-1/3 md:flex-1 min-w-[350px] max-w-[550px] p-[24px]">
              {currentGift && <GiftUser gift={currentGift} />}
            </div>
        
          </div>
        </div>
      </div>
    </>
  );
}

export default GiftDetailPage;
